import { Box, Button, SxProps, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { styled } from '@mui/material/styles';
import useCustomFileUpload from 'app/components/CustomFileUpload/useCustomFileUpload';
import BinIcon from 'app/components/Icons/BinIcon';
import DocumentIcon from 'app/components/Icons/DocumentIcon';
import FileUploadIcon from 'app/components/Icons/FileUploadIcon';

import AlertIcon from '../Icons/AlertIcon';
import Loader from '../Loader';
import { colors } from '../ThemeProvider/themeColors';
import {
  deleteBtn,
  fileBox,
  fileDetailsBox,
  fileDetailsInfo,
  fileDetailsInfoSize,
  fileDetailsSpanInfo,
  fileSizeText,
  header,
  inputProps,
  leftSide,
  leftSideFileInfo,
  uploadBox,
} from './CustomFileUpload.sx';

interface Props {
  fileSize: string;
  withOutLabel?: boolean;
  withOutDetails?: boolean;
  name?: any;
  file?: any;
  onDrop?: any;
  deleteFile?: any;
  errorFileSize?: boolean;
  isLoading?: boolean;
  acceptedFiles?: Record<string, string[]>;
  isExpired?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  uploadBoxSx?: SxProps;
}

const WarningBox = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  padding: '14px 16px',
  gap: 8,
  border: `1px solid  ${colors.error[300]}`,
  borderRadius: '10px',
  backgroundColor: colors.error[50],
  marginTop: '28px',
}));

const FileUpload = (props: Props) => {
  const {
    fileSize,
    withOutLabel,
    withOutDetails,
    file,
    onDrop,
    deleteFile,
    errorFileSize,
    isLoading,
    acceptedFiles,
    isExpired,
    disabled,
    sx,
    uploadBoxSx,
  } = props;
  const { t } = useTranslation();

  const { formatBytes, getRootProps, getInputProps } = useCustomFileUpload({
    onDrop,
    acceptedFiles,
    disabled,
  });

  return (
    <Box sx={sx}>
      {!withOutLabel && <Typography sx={header}>{t('commonTerms.additions')}</Typography>}
      <Box {...getRootProps()} sx={{ position: 'relative', width: '100%' }}>
        {isLoading && <Loader sx={{ position: 'absolute', left: '47%', zIndex: 1 }} />}
        <Box
          sx={[
            { opacity: isLoading || disabled ? '0.5' : '1', pointerEvents: disabled ? 'none' : 'auto' },
            uploadBox,
            ...(uploadBoxSx ? (Array.isArray(uploadBoxSx) ? uploadBoxSx : [uploadBoxSx]) : []),
          ]}
        >
          <FileUploadIcon />
          {t('commonTerms.selectFile')}
          <input type='file' {...getInputProps()} style={inputProps} />
        </Box>
      </Box>
      {!withOutDetails && (
        <Box sx={fileDetailsBox}>
          <Typography sx={fileDetailsInfo}>
            {t('commonTerms.allowedFileFormats')}
            <Typography component={'span'} sx={fileDetailsSpanInfo}>
              &nbsp;*.pdf, *.png, *.jpg/*.jpeg.
            </Typography>
          </Typography>
          <Typography sx={fileDetailsInfoSize(errorFileSize)}>
            {t('commonTerms.maxFileSize')}
            <Typography component={'span'} sx={fileDetailsSpanInfo}>
              {` ${fileSize}`}
            </Typography>
          </Typography>
        </Box>
      )}
      {file?.length > 0 &&
        file.map((item: any, index: number) => (
          <Box sx={fileBox} key={index}>
            <Box sx={leftSide}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                <DocumentIcon />
                <Box>
                  <Box sx={leftSideFileInfo}>
                    <Typography sx={{ lineHeight: '1', fontWeight: '500' }}>{item.name}</Typography>
                    <Typography sx={fileSizeText}>{formatBytes(item.size)}</Typography>
                  </Box>
                  {isExpired && (
                    <WarningBox>
                      <AlertIcon color={'error'} />
                      <Typography fontWeight={600} fontSize={16}>
                        Fayl aktual deyil, zəhmət olmasa yenidən yükləyin.
                      </Typography>
                    </WarningBox>
                  )}
                </Box>
              </Box>
            </Box>
            <Button color={'tertiary'} sx={deleteBtn} onClick={() => deleteFile(item, index)}>
              <BinIcon />
            </Button>
          </Box>
        ))}
    </Box>
    // <section className="container">
    //   <div {...getRootProps({ className: 'dropzone' })}>
    //     <input {...getInputProps()} />
    //     <p>Drag n drop some files here, or click to select files</p>
    //   </div>
    //   <aside>
    //     <h4>Files</h4>
    //     <ul>{files}</ul>
    //   </aside>
    // </section>
  );
};

export default FileUpload;
